import React, { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useConnectFourAI } from "./hooks/useConnectFourAI";
import { PIECE } from "./utils/c4Logic";
import ConnectFourBoard from "./ConnectFourBoard";
import ConnectFourStatusBar from "./ConnectFourStatusBar";
import ConnectFourResultModal from "./ConnectFourResultModal";
import useMeasuredAvailableHeight from "../../hooks/useMeasuredAvailableHeight";
import GameBoardFrame, { useGameBoardFrameMetrics } from "../games/GameBoardFrame";

export default function ConnectFourAIPage() {
  const { state, dropHuman, reset } = useConnectFourAI();
  const { board, currentTurn, winner, winCells, status, lastDrop, isThinking } = state;

  const isGameOver = status === "won" || status === "draw";
  const statusBarRef = useRef(null);
  const belowBoardRef = useRef(null);
  const boardMaxHeightPx = useMeasuredAvailableHeight(statusBarRef, belowBoardRef);
  const { isDesktop } = useGameBoardFrameMetrics();
  const boardMaxWidthStyle =
    boardMaxHeightPx != null
      ? `min(92vw, 640px, ${((boardMaxHeightPx * 7) / 6).toFixed(1)}px)`
      : "min(92vw, 640px)";

  return (
    <GameBoardFrame>
      <div className="w-full flex flex-col items-center">
        <div ref={statusBarRef} className="w-full">
          <ConnectFourStatusBar
            status={isGameOver ? (status === "draw" ? "draw" : "won") : isThinking ? "playing" : "playing"}
            currentTurn={currentTurn}
            myPiece={PIECE.ONE}
            winner={winner}
            p1Name="You"
            p2Name="AI"
            isAI
            maxWidthStyle={boardMaxWidthStyle}
          />
        </div>

        <ConnectFourBoard
          board={board}
          winCells={winCells}
          myPiece={PIECE.ONE}
          currentTurn={currentTurn}
          isGameOver={isGameOver}
          isDisabled={isThinking || isGameOver}
          lastDrop={lastDrop}
          onColumnClick={dropHuman}
          maxWidthStyle={boardMaxWidthStyle}
        />
      </div>

      <div ref={belowBoardRef} className="min-h-[1px] flex items-center justify-center">
        {!isGameOver && (
          <button
            type="button"
            onClick={reset}
            className="text-xs text-text-faint hover:text-text-secondary underline underline-offset-2 transition"
          >
            New game
          </button>
        )}
      </div>

      {isDesktop ? (
        <div className="w-full min-h-[76px] sm:min-h-[96px] flex items-start justify-center">
          <ConnectFourResultModal
            status={status}
            winner={winner}
            myPiece={PIECE.ONE}
            p1Name="You"
            p2Name="AI"
            isAI
            onPlayAgain={reset}
          />
        </div>
      ) : (
        // Mobile only: no reserved slot while the game is in progress -- a permanently
        // blank min-h box below the board reads as dead space. Animate it open only
        // once the game ends so the card eases into place instead of popping a static
        // gap that's been sitting empty the whole game.
        <AnimatePresence initial={false}>
          {isGameOver && (
            <motion.div
              className="w-full overflow-hidden flex items-start justify-center"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ConnectFourResultModal
                status={status}
                winner={winner}
                myPiece={PIECE.ONE}
                p1Name="You"
                p2Name="AI"
                isAI
                onPlayAgain={reset}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </GameBoardFrame>
  );
}
